import { Container, Graphics, Sprite, TilingSprite, type Texture } from 'pixi.js';
import { bakeFrames } from '../art/bake';

import { GALLOP_FRAMES_PER_M, HORSE_GALLOP, HORSE_IDLE } from '../art/sprites/horse';
import { DIRT_TILE, GRASS_TILE, STARTING_GATE } from '../art/sprites/scenery';
import { bindKeys } from '../input/InputController';
import { DustField, StrideEmitter } from '../render/effects';
import { VIRTUAL_H, VIRTUAL_W } from '../render/StageScaler';
import { xpFor } from '../sim/minigames/framework';
import { GatesSession } from '../sim/minigames/gates';
import { el } from '../ui/dom';
import {
  InputCollector,
  SessionRunner,
  launchPalette,
  showTrainingResults,
  trainingControls,
  type TrainingLaunch,
  type TrainingSceneOptions,
} from './minigameCommon';
import type { Scene, SceneContext } from './SceneManager';

const GROUND_Y = 216;
const GATE_X = 96;

const BURST_SPEED_PX = 220;

/** Acceleration training: wait for the bell, PRESS — never before. */
export class GatesScene implements Scene {
  private readonly launch: TrainingLaunch;
  private readonly options: TrainingSceneOptions;
  private ctx!: SceneContext;
  private session!: GatesSession;
  private runner!: SessionRunner;
  private collector!: InputCollector;
  private container!: Container;
  private horse!: Sprite;
  private idleTexture!: Texture;
  private gallopFrames!: Texture[];
  private hud!: HTMLElement;
  private roundText!: HTMLElement;
  private faultText!: HTMLElement;
  private statusText!: HTMLElement;
  private flash!: HTMLElement;
  private settleDock!: HTMLElement;
  private settleTarget!: HTMLElement;
  private settleNeedle!: HTMLElement;
  private unbind: (() => void) | null = null;
  private resultsShown = false;
  private disposeResults: (() => void) | null = null;
  private burstPx = 0;
  private bursting = false;
  private dust!: DustField;
  private readonly emitter = new StrideEmitter();
  private lastDrawMs = 0;

  constructor(launch: TrainingLaunch, options: TrainingSceneOptions) {
    this.launch = launch;
    this.options = options;
  }

  enter(ctx: SceneContext): void {
    this.ctx = ctx;
    this.session = new GatesSession(this.launch.tier, this.launch.seed);
    this.runner = new SessionRunner();
    this.collector = new InputCollector({ pressCodes: ['Enter', 'ArrowUp', 'KeyW'] });

    this.container = new Container();
    ctx.scaler.root.addChild(this.container);
    const sky = new Graphics()
      .rect(0, 0, VIRTUAL_W, 52)
      .fill(0x87a8c8)
      .rect(0, 52, VIRTUAL_W, 12)
      .fill(0x6f94b8);
    this.container.addChild(sky);
    const grass = new TilingSprite({
      texture: bakeFrames(GRASS_TILE.def, GRASS_TILE.palette)[0]!,
      width: VIRTUAL_W,
      height: 92,
    });
    grass.position.set(0, 64);
    this.container.addChild(grass);
    const dirt = new TilingSprite({
      texture: bakeFrames(DIRT_TILE.def, DIRT_TILE.palette)[0]!,
      width: VIRTUAL_W,
      height: VIRTUAL_H - 156,
    });
    dirt.position.set(0, 156);
    this.container.addChild(dirt);

    const gate = new Sprite(bakeFrames(STARTING_GATE.def, STARTING_GATE.palette)[0]!);
    gate.scale.set(1.4);
    gate.position.set(GATE_X - 60, GROUND_Y - STARTING_GATE.def.h * 1.4 + 4);
    this.container.addChild(gate);

    const coat = launchPalette(this.launch);
    this.dust = new DustField(this.container, 30);
    this.idleTexture = bakeFrames(HORSE_IDLE, coat)[0]!;
    this.gallopFrames = bakeFrames(HORSE_GALLOP, coat);
    this.horse = new Sprite(this.idleTexture);
    this.container.addChild(this.horse);

    this.roundText = el('span', { class: 'hud-chip' });
    this.faultText = el('span', { class: 'hud-chip bad' });
    this.statusText = el('div', { class: 'race-banner' });
    this.flash = el('div', { class: 'race-flash' });
    this.settleTarget = el('div', { class: 'gate-settle-target' });
    this.settleNeedle = el('div', { class: 'gate-settle-needle' });
    this.settleDock = el(
      'div',
      { class: 'gate-settle-dock' },
      el('span', { class: 'stamina-label' }, 'SETTLE'),
      el('div', { class: 'gate-settle-rail' }, this.settleTarget, this.settleNeedle),
    );
    this.hud = el(
      'div',
      { class: 'drill-hud with-training-controls' },
      el('div', { class: 'ranch-topbar' }, this.roundText, this.faultText),
      this.statusText,
      this.flash,
      this.settleDock,
      trainingControls(this.collector, [
        { action: 'primary', mode: 'press', label: 'BREAK ↑', ariaLabel: 'Break from the gate' },
      ]),
      el(
        'div',
        { class: 'ranch-hints' },
        'ENTER / ↑ / BREAK launches · wait for the bell · higher tiers call for one settle tap',
      ),
    );
    ctx.uiRoot.append(this.hud);

    this.unbind = bindKeys([{ code: 'Escape', handler: () => this.endEarly() }]);
  }

  frame(nowMs: number): void {
    const steps = this.runner.advance(nowMs, this.session, this.collector);
    for (const event of this.session.drainEvents()) {
      switch (event.kind) {
        case 'armed':
          this.bursting = false;
          this.burstPx = 0;
          this.statusText.textContent = 'Riders up…';
          break;
        case 'bell':
          this.showFlash('BREAK!', 'go');
          this.statusText.textContent = '';
          break;
        case 'distractor':
          this.showFlash('*flash*', 'dim');
          break;
        case 'break': {
          this.bursting = true;
          const ms = Math.round(event.reactionTicks * (1000 / 30));
          const word =
            event.grade >= 1 ? 'PERFECT' : event.grade >= 0.8 ? 'GREAT' : event.grade >= 0.5 ? 'GOOD' : 'SLOW';
          this.showFlash(this.launch.tier >= 4 ? `${word} — ${ms}ms` : word, event.grade >= 0.8 ? 'good' : 'go');
          if (this.session.config.settlePress) this.statusText.textContent = '…settle her!';
          break;
        }
        case 'settleHit':
          this.showFlash('SETTLED', 'good');
          this.statusText.textContent = '';
          break;
        case 'falseStart':
          this.bursting = false;
          this.showFlash('FALSE START!', 'bad');
          break;
        case 'miss':
          this.showFlash('SLEPT IN THE GATE', 'bad');
          break;
        default:
          break;
      }
    }
    if (this.bursting) this.burstPx += (steps / 30) * BURST_SPEED_PX;
    this.draw();
    if (this.session.finished && !this.resultsShown) this.showResults();
  }

  private draw(): void {
    const x = GATE_X + this.burstPx;
    const nowMs = performance.now();
    const dtS = this.lastDrawMs === 0 ? 0 : Math.min(0.05, (nowMs - this.lastDrawMs) / 1000);
    this.lastDrawMs = nowMs;
    this.dust.update(dtS);
    if (this.bursting && this.emitter.step(this.burstPx / 16, 2)) {
      this.dust.emit(x - 14, GROUND_Y, 1.3);
    }
    if (!this.bursting) this.emitter.reset();
    if (this.bursting) {
      this.horse.texture =
        this.gallopFrames[Math.floor((this.burstPx / 16) * GALLOP_FRAMES_PER_M) % this.gallopFrames.length]!;
    } else {
      this.horse.texture = this.idleTexture;
    }
    this.horse.position.set(Math.round(x) - HORSE_GALLOP.anchor[0], GROUND_Y - HORSE_GALLOP.h);
    this.roundText.textContent = `round ${Math.min(this.session.round + 1, this.session.config.rounds)}/${this.session.config.rounds}`;
    this.faultText.textContent = this.session.falseStarts > 0 ? '⚑'.repeat(this.session.falseStarts) : '';
    const settling = this.session.phase === 'settle';
    this.settleDock.classList.toggle('show', settling);
    if (settling) {
      const total = this.session.settleTargetTick + this.session.settleWindowTicks + 7;
      const start = (this.session.settleTargetTick - this.session.settleWindowTicks) / total;
      const width = (this.session.settleWindowTicks * 2) / total;
      this.settleTarget.style.left = `${(start * 100).toFixed(1)}%`;
      this.settleTarget.style.width = `${(width * 100).toFixed(1)}%`;
      this.settleNeedle.style.left = `${(Math.min(1, this.session.phaseElapsedTicks / total) * 100).toFixed(1)}%`;
      this.statusText.textContent = 'Tap when the white marker crosses gold';
    }
  }

  private endEarly(): void {
    if (this.session.finished) return;
    this.runner.fastForward(this.session);
  }

  private showFlash(text: string, tone: 'go' | 'good' | 'bad' | 'dim'): void {
    this.flash.textContent = text;
    this.flash.className = 'race-flash';
    void this.flash.offsetWidth;
    this.flash.className = `race-flash show ${tone === 'dim' ? 'go' : tone}`;
    if (tone === 'dim') this.flash.style.opacity = '0.5';
    else this.flash.style.opacity = '';
  }

  private showResults(): void {
    this.resultsShown = true;
    const score = this.session.score();
    const xp = xpFor(score.raw, this.launch.xpMult);
    const finish = (): void => {
      const tape = [...this.runner.tape];
      this.ctx.scenes.pop();
      this.options.onDone(tape);
    };
    this.disposeResults = showTrainingResults(
      this.ctx.uiRoot,
      { title: 'Gate work done', detail: score.detail, xp, failed: score.failed },
      finish,
    );
    this.unbind?.();
    this.unbind = bindKeys([
      { code: 'Enter', inModal: true, handler: finish },
      { code: 'Escape', inModal: true, handler: finish },
    ]);
  }

  exit(): void {
    this.unbind?.();
    this.collector.dispose();
    this.disposeResults?.();
    this.hud.remove();
    this.container.destroy({ children: true });
  }
}
